import React from 'react';
import { WarningOutlined, ClockCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { SmartHint } from '../../../types/client';

interface Props {
  hint: SmartHint;
}

interface HintCardProps {
  icon: React.ReactNode;
  message: string;
  iconColor: string;
  iconBg: string;
}

const HintCard: React.FC<HintCardProps> = ({ icon, message, iconColor, iconBg }) => (
  <div style={{
    margin: '0 16px 8px',
    borderRadius: 12,
    background: 'var(--bl-surface)',
    border: '1px solid var(--bl-divider)',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  }}>
    <div style={{
      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
      background: iconBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 15, color: iconColor,
    }}>
      {icon}
    </div>
    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--bl-text)', flex: 1 }}>
      {message}
    </span>
  </div>
);

const LOCALE_MAP: Record<string, string> = {
  uz: 'uz-UZ',
  ru: 'ru-RU',
  en: 'en-US',
};

export const SmartHintBlock: React.FC<Props> = ({ hint }) => {
  const { t, i18n } = useTranslation('customer');
  const locale = LOCALE_MAP[i18n.language] ?? 'uz-UZ';
  const { nearestBookingAt, pendingCount, cancellationRequestCount } = hint;

  if (pendingCount === 0 && cancellationRequestCount === 0 && nearestBookingAt === null) {
    return null;
  }

  if (cancellationRequestCount > 0) {
    return (
      <HintCard
        icon={<WarningOutlined />}
        message={t('bookings.smartHint.cancellationRequestCount', { count: cancellationRequestCount })}
        iconColor="rgba(217,119,6,1)"
        iconBg="rgba(217,119,6,0.10)"
      />
    );
  }

  if (pendingCount > 0) {
    return (
      <HintCard
        icon={<ClockCircleOutlined />}
        message={t('bookings.smartHint.pendingCount', { count: pendingCount })}
        iconColor="rgba(51,144,236,1)"
        iconBg="rgba(51,144,236,0.10)"
      />
    );
  }

  if (nearestBookingAt !== null) {
    const date = new Date(nearestBookingAt).toLocaleString(locale, {
      day: 'numeric', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    return (
      <HintCard
        icon={<CalendarOutlined />}
        message={t('bookings.smartHint.nearest', { date })}
        iconColor="rgba(51,144,236,1)"
        iconBg="rgba(51,144,236,0.10)"
      />
    );
  }

  return null;
};
